import React, { useState, useEffect } from "react";
import styles from "./ProfessionalRegistration.module.css";
import axiosInstance from "../../api/axiosConfig";
import { Link, useNavigate } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";

const schoolOptions = [
  "Colegio Médico del Perú",
  "Colegio de Enfermeros del Perú",
  "Colegio de Nutricionistas del Perú",
  "Colegio de Obstetras del Perú",
  "Colegio de Odontólogos del Perú",
  "Colegio de Psicólogos del Perú",
  "Colegio Médico Veterinario del Perú",
  "Colegio de Tecnólogos Médicos del Perú",
  "Asociación de Enfermeros Técnicos del Perú",
];

const specialtiesBySchool = {
  "Colegio de Nutricionistas del Perú": ["Nutrición"],
  "Colegio de Obstetras del Perú": ["Obstetricia"],
  "Colegio de Odontólogos del Perú": ["Odontología"],
  "Colegio de Psicólogos del Perú": ["Psicología"],
  "Asociación de Enfermeros Técnicos del Perú": ["Enfermería"],
};

const specialtiesMedica = [
  "Anestesiología / Terapia del Dolor", "Cardiología", "Cirugía de Cabeza, Cuello y Maxilofacial",
  "Cirugía de Tórax y Cardiovascular", "Cirugía General", "Cirugía Oncológica", "Cirugía Pediátrica",
  "Cirugía Plástica", "Dermatología", "Endocrinología", "Gastroenterología", "Genética", "Geriatría",
  "Ginecología y Obstetricia", "Hematología", "Inmunología y Alergia",
  "Medicina de Enfermedades Infecciosas y Tropicales", "Medicina Física y de Rehabilitación",
  "Medicina Interna", "Medicina Oncológica", "Neonatología", "Neurocirugía", "Neurología",
  "Nefrología", "Oftalmología", "Ortopedia y Traumatología", "Otorrinolaringología", "Pediatría",
  "Psiquiatría", "Radiología Intervencionista", "Reumatología", "Urología",
];

const specialtiesTecnologos = [
  "Terapia Física y Rehabilitación", "Terapia Respiratoria", "Terapia de Lenguaje",
  "Terapia de Aprendizaje", "Terapia Conductual", "Terapia Ocupacional",
];

const getSpecialtyOptions = (college) => {
  if (specialtiesBySchool[college]) return specialtiesBySchool[college];
  if (college === "Colegio Médico del Perú") return specialtiesMedica;
  if (college === "Colegio de Tecnólogos Médicos del Perú") return specialtiesTecnologos;
  return [];
};

const EyeIcon = ({ open }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    {open ? (
      <>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </>
    ) : (
      <>
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </>
    )}
  </svg>
);

const ProfessionalRegistration = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [formData, setFormData] = useState(() => {
    const saved = localStorage.getItem("professionalRegistrationData");
    const initial = {
      dni: "",
      genre: "",
      name: "",
      lastName: "",
      birthdate: "",
      phone: "",
      email: "",
      professional_college: "",
      registration_number: "",
      country: "",
      department: "",
      province: "",
      district: "",
      specialty: [],
      years_experience: "",
      emergencyServices: "",
      consultation_fee: "",
      password: "",
      confirm_password: "",
      termsAccepted: false,
      termsAcceptedAt: null,
    };
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.timestamp && parsed.data) {
          if (new Date().getTime() - parsed.timestamp < 10 * 60 * 1000) {
            return { ...initial, ...parsed.data };
          }
          localStorage.removeItem("professionalRegistrationData");
        }
      } catch {}
    }
    return initial;
  });

  useEffect(() => {
    localStorage.setItem(
      "professionalRegistrationData",
      JSON.stringify({ data: formData, timestamp: new Date().getTime() })
    );
  }, [formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "professional_college") {
      const autoSpecialty = specialtiesBySchool[value] || [];
      setFormData((prev) => ({ ...prev, [name]: value, specialty: autoSpecialty }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleCheckboxChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      termsAccepted: e.target.checked,
      termsAcceptedAt: e.target.checked ? new Date() : null,
    }));
  };

  const goNext = (e) => {
    e.preventDefault();
    setStep((s) => s + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goBack = () => {
    setStep((s) => s - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.termsAccepted) {
      alert("Debes aceptar los términos y condiciones");
      return;
    }
    if (formData.password !== formData.confirm_password) {
      alert("Las contraseñas no coinciden");
      return;
    }
    setLoading(true);
    try {
      await axiosInstance.post("/api/register-professional", formData);
      localStorage.removeItem("professionalRegistrationData");
      navigate("/registro/profesional-exitosa");
    } catch (error) {
      console.error("Error al enviar el formulario:", error);
      if (error.response?.status === 404) {
        alert("El email ya está registrado como profesional");
      }
    } finally {
      setLoading(false);
    }
  };

  const specialtyOptions = getSpecialtyOptions(formData.professional_college);

  return (
    <div className={styles.page}>
      {/* ── Header ── */}
      <div className={styles.pageHeader}>
        <div className={styles.headerInner}>
          <div className={styles.breadcrumb}>
            <Link to="/">Inicio</Link> / <span>Registro de Profesional</span>
          </div>

          <div className={styles.headerRow}>
            <div className={styles.headerLeft}>
              <h1>Registro de Profesional</h1>
              <p>Completá tus datos para unirte como profesional de salud.</p>
            </div>
            <span className={styles.loginHint}>
              ¿Ya tenés cuenta? <Link to="/login">Iniciá sesión →</Link>
            </span>
          </div>

          {/* Stepper */}
          <div className={styles.stepper}>
            <div className={`${styles.stepItem} ${step >= 1 ? styles.active : ""}`}>
              <div className={styles.stepCircle}>1</div>
              Datos personales
            </div>
            <div className={`${styles.stepLine} ${step >= 2 ? styles.doneLine : ""}`} />
            <div className={`${styles.stepItem} ${step >= 2 ? styles.active : ""}`}>
              <div className={styles.stepCircle}>2</div>
              Datos profesionales
            </div>
            <div className={`${styles.stepLine} ${step >= 3 ? styles.doneLine : ""}`} />
            <div className={`${styles.stepItem} ${step === 3 ? styles.active : ""}`}>
              <div className={styles.stepCircle}>3</div>
              Acceso y confirmación
            </div>
          </div>
        </div>
      </div>

      {/* ── Contenido ── */}
      <div className={styles.content}>

        {/* ────── PASO 1 ────── */}
        {step === 1 && (
          <form onSubmit={goNext}>
            <div className={styles.card}>
              <p className={styles.stepLabel}>PASO 1 DE 3</p>
              <h2 className={styles.cardTitle}>Datos personales</h2>
              <p className={styles.cardSubtitle}>Información personal del profesional de salud.</p>

              <div className={styles.formGrid}>
                <div className={styles.field}>
                  <label className={styles.label}>DNI*</label>
                  <input className={styles.input} name="dni" value={formData.dni} onChange={handleChange} placeholder="00000000" required />
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>GÉNERO*</label>
                  <select className={styles.select} name="genre" value={formData.genre} onChange={handleChange} required>
                    <option value="">Seleccionar</option>
                    <option value="Masculino">Masculino</option>
                    <option value="Femenino">Femenino</option>
                    <option value="Otro">Otro</option>
                  </select>
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>NOMBRES*</label>
                  <input className={styles.input} name="name" value={formData.name} onChange={handleChange} placeholder="Tus nombres" required />
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>APELLIDOS*</label>
                  <input className={styles.input} name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Tus apellidos" required />
                </div>

                <div className={styles.field}>
                  <label className={styles.label}>FECHA DE NACIMIENTO*</label>
                  <input className={styles.input} type="date" name="birthdate" value={formData.birthdate} onChange={handleChange} required />
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>TELÉFONO*</label>
                  <input className={styles.input} type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="+51 999 999 999" required />
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>CORREO ELECTRÓNICO*</label>
                  <input className={styles.input} type="email" name="email" value={formData.email} onChange={handleChange} placeholder="tucorreo@email.com" required />
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>COLEGIO PROFESIONAL*</label>
                  <select className={styles.select} name="professional_college" value={formData.professional_college} onChange={handleChange} required>
                    <option value="">Seleccionar</option>
                    {schoolOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>

                <div className={styles.field}>
                  <label className={styles.label}>NÚMERO DE COLEGIO PROFESIONAL*</label>
                  <input className={styles.input} name="registration_number" value={formData.registration_number} onChange={handleChange} placeholder="N.° de colegiatura" required />
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>PAÍS</label>
                  <input className={styles.input} name="country" value={formData.country} onChange={handleChange} placeholder="Perú" />
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>DEPARTAMENTO</label>
                  <input className={styles.input} name="department" value={formData.department} onChange={handleChange} />
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>PROVINCIA</label>
                  <input className={styles.input} name="province" value={formData.province} onChange={handleChange} />
                </div>

                <div className={styles.field}>
                  <label className={styles.label}>DISTRITO</label>
                  <input className={styles.input} name="district" value={formData.district} onChange={handleChange} />
                </div>
                {/* celda vacía para mantener el grid */}
                <div /><div /><div />
              </div>

              <p className={styles.required}>* Campos obligatorios</p>

              <div className={styles.cardFooter}>
                <Link to="/login" className={styles.btnBack}>← Volver al login</Link>
                <button type="submit" className={styles.btnNext}>Siguiente →</button>
              </div>
            </div>
          </form>
        )}

        {/* ────── PASO 2 ────── */}
        {step === 2 && (
          <form onSubmit={goNext}>
            <div className={styles.card}>
              <p className={styles.stepLabel}>PASO 2 DE 3</p>
              <h2 className={styles.cardTitle}>Datos profesionales</h2>
              <p className={styles.cardSubtitle}>Información sobre tu práctica profesional.</p>

              <div className={styles.formGrid}>
                <div className={styles.field}>
                  <label className={styles.label}>COLEGIO PROFESIONAL*</label>
                  <select className={styles.select} name="professional_college" value={formData.professional_college} onChange={handleChange} required>
                    <option value="">Seleccionar</option>
                    {schoolOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>N.° DE COLEGIO*</label>
                  <input className={styles.input} name="registration_number" value={formData.registration_number} onChange={handleChange} placeholder="Número de colegiatura" required />
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>ESPECIALIDAD PRINCIPAL</label>
                  <select className={styles.select} name="specialty" value={formData.specialty[0] || ""} onChange={(e) => setFormData((prev) => ({ ...prev, specialty: [e.target.value] }))}>
                    <option value="">Seleccionar</option>
                    {specialtyOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>AÑOS DE EXPERIENCIA</label>
                  <input className={styles.input} type="number" name="years_experience" value={formData.years_experience} onChange={handleChange} placeholder="0" min="0" />
                </div>

                <div className={styles.field}>
                  <label className={styles.label}>MODALIDAD DE ATENCIÓN</label>
                  <select className={styles.select} name="emergencyServices" value={formData.emergencyServices} onChange={handleChange}>
                    <option value="">Seleccionar</option>
                    <option value="Presencial">Presencial</option>
                    <option value="Virtual">Virtual</option>
                    <option value="Ambas">Ambas</option>
                  </select>
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>TARIFA DE CONSULTA (S/)</label>
                  <input className={styles.input} type="number" name="consultation_fee" value={formData.consultation_fee} onChange={handleChange} placeholder="0.00" min="0" step="0.01" />
                </div>
                <div /><div />
              </div>

              <p className={styles.required}>* Campos obligatorios</p>

              <div className={styles.cardFooter}>
                <button type="button" className={styles.btnBack} onClick={goBack}>← Atrás</button>
                <button type="submit" className={styles.btnNext}>Siguiente →</button>
              </div>
            </div>
          </form>
        )}

        {/* ────── PASO 3 ────── */}
        {step === 3 && (
          <form onSubmit={handleSubmit}>
            {/* Card 1: campos de acceso */}
            <div className={styles.card}>
              <p className={styles.stepLabel}>PASO 3 DE 3</p>
              <h2 className={styles.cardTitle}>Acceso y confirmación</h2>
              <p className={styles.cardSubtitle}>Configurá tus credenciales de acceso.</p>

              <div className={styles.formGrid3}>
                <div className={styles.field}>
                  <label className={styles.label}>CORREO ELECTRÓNICO*</label>
                  <input className={styles.input} type="email" name="email" value={formData.email} onChange={handleChange} placeholder="tucorreo@email.com" required />
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>CONTRASEÑA*</label>
                  <div className={styles.passwordWrap}>
                    <input className={styles.input} type={showPassword ? "text" : "password"} name="password" value={formData.password} onChange={handleChange} placeholder="Mínimo 8 caracteres" required minLength={8} />
                    <button type="button" className={styles.eyeBtn} onClick={() => setShowPassword((v) => !v)}>
                      <EyeIcon open={showPassword} />
                    </button>
                  </div>
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>CONFIRMAR CONTRASEÑA*</label>
                  <div className={styles.passwordWrap}>
                    <input className={styles.input} type={showConfirm ? "text" : "password"} name="confirm_password" value={formData.confirm_password} onChange={handleChange} placeholder="Repetir contraseña" required minLength={8} />
                    <button type="button" className={styles.eyeBtn} onClick={() => setShowConfirm((v) => !v)}>
                      <EyeIcon open={showConfirm} />
                    </button>
                  </div>
                </div>
              </div>

              <p className={styles.required}>* Campos obligatorios</p>
            </div>

            {/* Card 2: términos + botones */}
            <div className={styles.card}>
              <div className={styles.cardFooter}>
                <button type="button" className={styles.btnBack} onClick={goBack}>← Atrás</button>
                <div className={styles.cardFooterRight}>
                  <label className={styles.checkRow}>
                    <input type="checkbox" checked={formData.termsAccepted} onChange={handleCheckboxChange} required />
                    Acepto los <Link to="/terminos-condiciones">términos y condiciones</Link> de Vitalclic *
                  </label>
                  <button type="submit" className={styles.btnSubmit} disabled={loading}>
                    {loading
                      ? <CircularProgress size={16} thickness={5} sx={{ color: "#fff" }} />
                      : "✓ REGISTRARME"}
                  </button>
                </div>
              </div>
            </div>
          </form>
        )}

      </div>
    </div>
  );
};

export default ProfessionalRegistration;
